import React, { FC, ReactNode } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"

import { CtaFragmentFragment } from "@graphqlTypes"

import { generateConditionalClassName } from "@utils"

import AnchorLink from "@components/AnchorLink/AnchorLink"
import Button, { BUTTON_SIZES } from "@components/Button/Button"
import HubspotForm from "@components/HubspotForm/HubspotForm"
import TextLink, { TEXT_LINK_SIZES } from "@components/TextLink/TextLink"

import "./CTA.scss"
import { CSSProperties } from "styled-components"

type CtaWrapperProps = {
  children: ReactNode,
  className: string,
  hasExternalLink?: boolean,
  hasForm: boolean,
  link?: string | null,
  style: CSSProperties,
}

const CTAWrapper: FC<CtaWrapperProps> = ({
  className,
  children,
  link,
  hasForm,
  style,
}: CtaWrapperProps) => {
  if (hasForm || !link) return <div className={className} style={style}>{children}</div>
  return <AnchorLink className={className} style={style} href={link}>{children}</AnchorLink>
}

const CTA: FC<CtaFragmentFragment & { isCompact?: boolean }> = ({
  buttonText,
  color,
  colorHex,
  ctaDescription,
  hubspotFormData,
  image,
  isCompact,
  link,
  overline,
  pdfLink,
  title,
  useFullWidth,
  useLightText,
  useTextLink,
}) => {
  if (!link && !hubspotFormData) return null
  return (
    <CTAWrapper
      className={generateConditionalClassName({
        CTA: true,
        "use-full-width": !!useFullWidth,
        "use-light-text": !!useLightText,
        "is-link": !!link,
        "is-compact": !!isCompact,
      })}
      hasForm={!!hubspotFormData}
      link={link}
      style={{
        "--CTA--background": colorHex ? colorHex : color ? `var(--color--${color}-100)` : `var(--color--slate-100)`,
      } as CSSProperties}
    >
      <div className="CTA--content">
        {overline && <h5 className="type--mono-L color--text-default">{overline}</h5>}
        {title && <h4>{title}</h4>}
        {ctaDescription && <p>{ctaDescription}</p>}
        {!hubspotFormData && link && (
          useTextLink ?
            <TextLink
              size={isCompact ? TEXT_LINK_SIZES.M : TEXT_LINK_SIZES.L}
              useDarkMode={useLightText}
            >
              {buttonText || "Learn More"}
            </TextLink> :
            <Button
              className="CTA--Button"
              size={isCompact ? BUTTON_SIZES.M : BUTTON_SIZES.L}
            >
              {buttonText || "Learn More"}
            </Button>
        )}
        {!(useFullWidth && !image) && hubspotFormData && (
          <HubspotForm
            {...hubspotFormData}
            redirectUrl={pdfLink}
          />
        )}
      </div>
      {useFullWidth && !image && hubspotFormData && (
        <div className="CTA--form">
          <HubspotForm
            {...hubspotFormData}
            redirectUrl={pdfLink}
          />
        </div>
      )}
      {image && image.imgixImage && (
        <div className="CTA--image">
          <GatsbyImage
            alt={title || ""}
            image={image.imgixImage.gatsbyImageData}
            role="presentation"
          />
        </div>
      )}
    </CTAWrapper>
  )
}

export const CTAFragment = graphql`
  fragment CtaFragment on ContentfulCta {
    contentful_id
    buttonText
    color
    colorHex
    ctaDescription: description # aliasing this field name otherwise graphql throws conflicting type error
    hubspotFormData {
      ...HubspotFormFragment
    }
    image {
      imgixImage {
        gatsbyImageData(width: 616, layout: CONSTRAINED)
      }
      height
      width
    }
    link
    overline
    pdfLink
    title
    useFullWidth
    useLightText
    useTextLink
    blockBackground
    blockIncludeBackground
    blockMarginBottom
    blockUseDarkMode
  }
`

export default CTA
